import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleFilled, EyeInvisibleOutlined, EyeOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons'
import { registration, type PhoneSession } from '../api'
import AuthShell, { AuthField, BackToSignIn } from '../components/AuthShell'

type Step =
  | { at: 'ask' }
  | { at: 'sent'; to: string }
  | { at: 'sms'; session: PhoneSession }
  | { at: 'set'; token: string }
  | { at: 'done'; account: string }

/**
 * Forgetting a password.
 *
 * The proof is whichever channel the account already confirmed — a link to the
 * address on file, or an SMS from the handset on file. Nothing here accepts a
 * new address or number, because that would make a reset a way in rather than
 * a way back.
 */
export default function Forgot({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation()
  const [channel, setChannel] = useState<'email' | 'phone'>('email')
  const [can, setCan] = useState({ email: true, phone: true })
  const [identifier, setIdentifier] = useState('')
  const [step, setStep] = useState<Step>({ at: 'ask' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    registration.options()
      .then(o => { setCan(o.confirm); setChannel(o.confirm.email ? 'email' : 'phone') })
      .catch(() => { /* leave both on offer; the server still refuses clearly */ })
  }, [])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!identifier.trim() || busy) return
    setBusy(true); setError('')
    try {
      const r = await registration.resetStart(identifier.trim(), channel)
      if (r.phone) setStep({ at: 'sms', session: r.phone })
      else setStep({ at: 'sent', to: r.sent_to ?? '' })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false) }
  }

  if (step.at === 'sent') {
    return (
      <AuthShell title={t('checkYourEmail')} lede={t('resetSentTo', { email: step.to })}
        foot={<BackToSignIn onClick={onDone} />}>
        <p className="auth-lede muted">{t('resetLinkOnce')}</p>
      </AuthShell>
    )
  }

  if (step.at === 'sms') {
    return <SmsStep session={step.session} onProven={token => setStep({ at: 'set', token })} onCancel={onDone} />
  }

  if (step.at === 'set') {
    return <NewPassword token={step.token} onDone={account => setStep({ at: 'done', account })} />
  }

  if (step.at === 'done') {
    return (
      <AuthShell title={t('passwordChanged')} lede={t('signInWithNew')}>
        <p className="verified-mark"><CheckCircleFilled /></p>
        <button className="auth-submit" type="button" onClick={onDone}>{t('signIn')}</button>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      as="form" onSubmit={submit}
      title={t('forgotTitle')}
      lede={t('forgotLede')}
      foot={<BackToSignIn onClick={onDone} />}
    >
      {error && <div className="auth-error" role="alert"><WarningOutlined /> <span>{error}</span></div>}

      <fieldset className="choice">
        <legend>{t('confirmWith')}</legend>
        <div className="choice-row">
          {(['email', 'phone'] as const).map(c => (
            <button key={c} type="button" disabled={!can[c]}
              className={channel === c ? 'on' : ''} aria-pressed={channel === c}
              title={can[c] ? undefined : t('channelUnavailable')}
              onClick={() => { setChannel(c); setIdentifier('') }}>
              {c === 'email' ? t('email') : t('phone')}
            </button>
          ))}
        </div>
        {!(can.email && can.phone) && <span className="field-hint">{t('channelUnavailable')}</span>}
      </fieldset>

      <AuthField
        id="f-id"
        label={channel === 'email' ? t('email') : t('phone')}
        hint={channel === 'email' ? t('forgotEmailHint') : t('forgotPhoneHint')}
        value={identifier} onChange={setIdentifier}
        type={channel === 'email' ? 'email' : 'tel'}
        inputMode={channel === 'email' ? 'email' : 'tel'}
        autoComplete={channel === 'email' ? 'email' : 'tel'}
      />

      <button className="auth-submit" type="submit" disabled={busy || !identifier.trim()}>
        {busy ? <LoadingOutlined /> : t('sendResetLink')}
      </button>
    </AuthShell>
  )
}

/** The verify.mn step, in reset clothing: the same proof, a different reason. */
function SmsStep({ session, onProven, onCancel }: {
  session: PhoneSession; onProven: (token: string) => void; onCancel: () => void
}) {
  const { t } = useTranslation()
  const [left, setLeft] = useState(() => secondsLeft(session.expires_at))
  const [expired, setExpired] = useState(false)
  const stop = useRef(false)

  useEffect(() => {
    stop.current = false
    const poll = setInterval(async () => {
      if (stop.current) return
      try {
        const r = await registration.resetPollPhone(session.session_id)
        if (r.verified && r.token) { stop.current = true; onProven(r.token) }
        else if (r.status === 'EXPIRED') { stop.current = true; setExpired(true) }
      } catch { /* transient — keep asking until it expires */ }
    }, 3000)
    const clock = setInterval(() => {
      const s = secondsLeft(session.expires_at)
      setLeft(s)
      if (s <= 0 && !stop.current) { stop.current = true; setExpired(true) }
    }, 1000)
    return () => { stop.current = true; clearInterval(poll); clearInterval(clock) }
  }, [session.session_id, session.expires_at, onProven])

  return (
    <AuthShell title={t('confirmPhone')} lede={session.instruction}
      foot={<BackToSignIn onClick={onCancel} />}>
      <div className="sms-code">
        <span className="sms-label">{t('sendThis')}</span>
        <b className="num">{session.code}</b>
        <span className="sms-label">{t('toShortcode', { shortcode: session.shortcode })}</span>
      </div>
      {expired ? (
        <div className="auth-error" role="alert"><WarningOutlined /> <span>{t('codeExpired')}</span></div>
      ) : (
        <>
          <a className="auth-submit as-link" href={session.sms_uri}>{t('openMessages')}</a>
          <p className="auth-lede muted">{t('smsCost')}</p>
          <p className="auth-lede muted"><LoadingOutlined /> {t('waitingForSms', { seconds: left })}</p>
        </>
      )}
    </AuthShell>
  )
}

/** Choosing the new password, once the channel has been satisfied. */
function NewPassword({ token, onDone }: { token: string; onDone: (account: string) => void }) {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const mismatch = again.length > 0 && password !== again
  const ready = password.length >= 10 && password === again

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!ready || busy) return
    setBusy(true); setError('')
    try {
      const r = await registration.resetComplete(token, password)
      onDone(r.account)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setBusy(false) }
  }

  return (
    <AuthShell as="form" onSubmit={submit} title={t('chooseNewPassword')} lede={t('chooseNewLede')}>
      {error && <div className="auth-error" role="alert"><WarningOutlined /> <span>{error}</span></div>}

      <AuthField id="np" label={t('newPassword')} hint={t('passwordHint')}
        value={password} onChange={setPassword}
        type={show ? 'text' : 'password'} autoComplete="new-password">
        <button type="button" className="icon-btn" onClick={() => setShow(s => !s)}
          aria-label={show ? t('hidePassword') : t('showPassword')}>
          {show ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        </button>
      </AuthField>

      <AuthField id="np2" label={t('repeatPassword')}
        error={mismatch ? t('passwordsDiffer') : undefined}
        value={again} onChange={setAgain}
        type={show ? 'text' : 'password'} autoComplete="new-password" />

      <button className="auth-submit" type="submit" disabled={busy || !ready}>
        {busy ? <LoadingOutlined /> : t('setPassword')}
      </button>
    </AuthShell>
  )
}

/** The landing for the emailed link. Public, because that is the whole point. */
export function ResetFromLink({ token, onDone }: { token: string; onDone: () => void }) {
  const { t } = useTranslation()
  const [account, setAccount] = useState('')
  if (account) {
    return (
      <AuthShell title={t('passwordChanged')} lede={t('signInWithNew')}>
        <p className="verified-mark"><CheckCircleFilled /></p>
        <button className="auth-submit" type="button" onClick={onDone}>{t('signIn')}</button>
      </AuthShell>
    )
  }
  return <NewPassword token={token} onDone={setAccount} />
}

function secondsLeft(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now()
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 1000)) : 0
}
